import React, { useState } from 'react';
import axios from 'axios';
import { useAdminAuth } from '../../contexts/AdminAuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProgresoTab({ userData, refreshData }) {
  const { getToken } = useAdminAuth();
  const { user, progress, projects, certificates } = userData;
  const [selectedProject, setSelectedProject] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showCertificateModal, setShowCertificateModal] = useState(false);

  const totalProgress = progress?.length > 0
    ? Math.round(progress.reduce((acc, p) => acc + (p.percentage || 0), 0) / progress.length)
    : 0;

  const handleReviewProject = (project) => {
    setSelectedProject(project);
    setShowReviewModal(true);
  };

  const handleIssueCertificate = () => {
    setShowCertificateModal(true);
  };

  return (
    <div className="space-y-6">
      {/* Overall Progress */}
      <div className="bg-gradient-to-r from-[#D4AF37]/20 to-[#1a4d5c]/20 rounded-lg p-6 border border-[#D4AF37]/30">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-2xl font-bold text-white">Progreso General</h3>
          <div className="text-4xl font-bold text-[#D4AF37]">{totalProgress}%</div>
        </div>
        <div className="w-full bg-black/50 rounded-full h-4">
          <div
            className="bg-gradient-to-r from-[#D4AF37] to-[#F4E4B7] h-4 rounded-full transition-all"
            style={{ width: `${totalProgress}%` }}
          ></div>
        </div>
      </div>

      {/* Progress by Pillar */}
      {progress && progress.length > 0 ? (
        <div className="bg-black/30 rounded-lg p-6 border border-[#1a4d5c]/30">
          <h4 className="font-semibold text-white mb-4">Progreso por Pilar</h4>
          <div className="space-y-4">
            {progress.map((p) => (
              <div key={p.pillar_id}>
                <div className="flex justify-between mb-2">
                  <span className="text-white">{p.pillar_name || p.pillar_id}</span>
                  <span className="text-[#D4AF37] font-bold">{p.percentage || 0}%</span>
                </div>
                <div className="w-full bg-[#1a4d5c]/30 rounded-full h-2">
                  <div
                    className="bg-gradient-to-r from-[#D4AF37] to-[#F4E4B7] h-2 rounded-full transition-all"
                    style={{ width: `${p.percentage || 0}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="text-center py-8 bg-black/30 rounded-lg border border-[#1a4d5c]/30">
          <p className="text-gray-400">Sin progreso registrado</p>
        </div>
      )}

      {/* Projects */}
      <div className="bg-black/30 rounded-lg p-6 border border-[#1a4d5c]/30">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-semibold text-white">Proyectos ({projects?.length || 0})</h4>
          <div className="flex gap-2 text-xs">
            <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded">
              {projects?.filter(p => p.status === 'in_review').length || 0} en revisión
            </span>
            <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded">
              {projects?.filter(p => p.status === 'approved').length || 0} aprobados
            </span>
          </div>
        </div>
        {projects && projects.length > 0 ? (
          <div className="space-y-3">
            {projects.map((project) => (
              <div key={project.id} className="p-4 bg-black/50 rounded-lg border border-[#1a4d5c]/30">
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-1">
                    <h5 className="text-white font-medium">{project.title || 'Proyecto'}</h5>
                    <p className="text-gray-400 text-sm">
                      Pilar: {project.pillar_id} • Módulo: {project.module_id || 'N/A'}
                    </p>
                    {project.submitted_at && (
                      <p className="text-gray-500 text-xs mt-1">
                        Enviado: {new Date(project.submitted_at).toLocaleDateString('es-ES')}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      project.status === 'approved' ? 'bg-green-500/20 text-green-400' :
                      project.status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                      project.status === 'in_review' ? 'bg-yellow-500/20 text-yellow-400' :
                      'bg-gray-500/20 text-gray-400'
                    }`}>
                      {project.status}
                    </span>
                    {project.status === 'in_review' && (
                      <button
                        onClick={() => handleReviewProject(project)}
                        className="px-3 py-1 bg-[#D4AF37]/20 hover:bg-[#D4AF37]/30 text-[#D4AF37] rounded text-xs"
                      >
                        Revisar
                      </button>
                    )}
                  </div>
                </div>
                {project.score !== undefined && project.score !== null && (
                  <div className="flex items-center gap-2 mt-2">
                    <div className="text-[#D4AF37] font-bold">Score: {project.score}/100</div>
                    <div className="flex-1 bg-black/50 rounded-full h-2">
                      <div
                        className="bg-[#D4AF37] h-2 rounded-full"
                        style={{ width: `${project.score}%` }}
                      ></div>
                    </div>
                  </div>
                )}
                {project.feedback && (
                  <div className="mt-2 p-3 bg-black/50 rounded text-sm text-gray-300 italic border-l-2 border-[#D4AF37]">
                    {project.feedback}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-400 text-center py-4">No hay proyectos</p>
        )}
      </div>

      {/* Certificates */}
      <div className="bg-black/30 rounded-lg p-6 border border-[#1a4d5c]/30">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-semibold text-white">Certificados ({certificates?.length || 0})</h4>
          <button
            onClick={handleIssueCertificate}
            className="px-4 py-2 bg-[#D4AF37]/20 hover:bg-[#D4AF37]/30 text-[#D4AF37] rounded-lg text-sm border border-[#D4AF37]/50"
          >
            + Emitir Certificado
          </button>
        </div>
        {certificates && certificates.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {certificates.map((cert) => (
              <div key={cert.id} className="p-4 bg-[#D4AF37]/10 rounded-lg border border-[#D4AF37]/30">
                <div className="flex items-center gap-3">
                  <div className="text-3xl">🏆</div>
                  <div className="flex-1">
                    <div className="text-white font-semibold">
                      {cert.program_certificate ? '🎓 Certificado del Programa' : `📜 Pilar: ${cert.pillar_id}`}
                    </div>
                    <div className="text-gray-400 text-sm">
                      Emitido: {new Date(cert.issued_at).toLocaleDateString('es-ES')}
                    </div>
                  </div>
                  {cert.download_url && (
                    <button
                      onClick={() => window.open(cert.download_url, '_blank')}
                      className="px-3 py-1 bg-[#D4AF37]/20 hover:bg-[#D4AF37]/30 text-[#D4AF37] rounded text-xs"
                    >
                      📥
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-400 text-center py-4">No hay certificados emitidos</p>
        )}
      </div>

      {/* Modals */}
      {showReviewModal && selectedProject && (
        <ProjectReviewModal
          project={selectedProject}
          userId={user.id}
          onClose={() => {
            setShowReviewModal(false);
            setSelectedProject(null);
          }}
          onSuccess={() => {
            setShowReviewModal(false);
            setSelectedProject(null);
            refreshData();
          }}
        />
      )}

      {showCertificateModal && (
        <IssueCertificateModal
          userId={user.id}
          pillars={progress || []}
          onClose={() => setShowCertificateModal(false)}
          onSuccess={() => {
            setShowCertificateModal(false);
            refreshData();
          }}
        />
      )}
    </div>
  );
}

// Project Review Modal
function ProjectReviewModal({ project, userId, onClose, onSuccess }) {
  const { getToken } = useAdminAuth();
  const [status, setStatus] = useState('approved');
  const [score, setScore] = useState(project.score || 85);
  const [feedback, setFeedback] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      await axios.patch(
        `${BACKEND_URL}/api/admin/projects/${project.id}`,
        { status, score: parseInt(score), feedback },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      alert('Proyecto revisado correctamente');
      onSuccess();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al revisar proyecto');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80">
      <div className="bg-[#0A0A0A] rounded-lg max-w-md w-full p-6 border border-[#1a4d5c]/30">
        <h3 className="text-xl font-bold text-white mb-4">Revisar Proyecto</h3>
        <div className="space-y-4">
          <div className="bg-black/50 rounded-lg p-3">
            <div className="text-sm text-gray-400">Proyecto</div>
            <div className="text-white font-semibold">{project.title}</div>
          </div>
          
          <div>
            <label className="block text-sm text-gray-300 mb-2">Estado</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white"
            >
              <option value="approved">✅ Aprobado</option>
              <option value="rejected">❌ Rechazado</option>
              <option value="in_review">🔄 Revisar más</option>
            </select>
          </div>

          {status === 'approved' && (
            <div>
              <label className="block text-sm text-gray-300 mb-2">Puntuación (0-100)</label>
              <input
                type="number"
                min="0"
                max="100"
                value={score}
                onChange={(e) => setScore(e.target.value)}
                className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white"
              />
            </div>
          )}

          <div>
            <label className="block text-sm text-gray-300 mb-2">Feedback</label>
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              rows="4"
              placeholder="Feedback para el estudiante..."
              className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white"
            />
          </div>

          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
            >
              Cancelar
            </button>
            <button
              onClick={handleSubmit}
              disabled={isLoading}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-[#D4AF37] to-[#F4E4B7] text-black rounded-lg disabled:opacity-50"
            >
              {isLoading ? 'Guardando...' : 'Guardar Revisión'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Issue Certificate Modal
function IssueCertificateModal({ userId, pillars, onClose, onSuccess }) {
  const { getToken } = useAdminAuth();
  const [type, setType] = useState('program');
  const [pillarId, setPillarId] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleIssue = async () => {
    setIsLoading(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/users/${userId}/certificate`,
        {
          type,
          pillar_id: type === 'pillar' ? pillarId : null
        },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      alert('Certificado emitido correctamente');
      onSuccess();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al emitir certificado');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80">
      <div className="bg-[#0A0A0A] rounded-lg max-w-md w-full p-6 border border-[#1a4d5c]/30">
        <h3 className="text-xl font-bold text-white mb-4">Emitir Certificado</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-gray-300 mb-2">Tipo de Certificado</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white"
            >
              <option value="program">🎓 Certificado del Programa Completo</option>
              <option value="pillar">📜 Certificado de Pilar Individual</option>
            </select>
          </div>

          {type === 'pillar' && (
            <div>
              <label className="block text-sm text-gray-300 mb-2">Seleccionar Pilar</label>
              <select
                value={pillarId}
                onChange={(e) => setPillarId(e.target.value)}
                className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white"
              >
                <option value="">Selecciona un pilar...</option>
                {pillars.map((p) => (
                  <option key={p.pillar_id} value={p.pillar_id}>
                    {p.pillar_name || p.pillar_id} ({p.percentage}% completado)
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
            >
              Cancelar
            </button>
            <button
              onClick={handleIssue}
              disabled={isLoading || (type === 'pillar' && !pillarId)}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-[#D4AF37] to-[#F4E4B7] text-black rounded-lg disabled:opacity-50"
            >
              {isLoading ? 'Emitiendo...' : 'Emitir Certificado'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}